import GroupObject from "../objects/group"
import Calc from "@/common/util/calculator"
import BaseFixmethod, { type FixmethodPosition } from "@/signomatic/app/fixmethods/base"
import CircleObject from "@/signomatic/app/objects/circle"
import ImageObject from "@/signomatic/app/objects/image"

export default class WallDistanceFixmethod extends BaseFixmethod {
  get count() {
    return this.positions().filter(e => !e.stretch).length
  }

  topDrawable() {
    const items = []
    const imgEl = this.image()
    const screwDiameter = Calc.mmToPx(4.3)

    if (this.imagesLoaded && imgEl) {
      for (const e of this.positions()) {
        if (e.stretch === true) {
          items.push(
            new CircleObject({
              left: e.left,
              top: e.top,
              radius: (e.diameter || 0) / 2,
              stroke: "transparent",
              strokeWidth: 1,
              fill: "transparent",
            })
          )

          continue
        }

        const img = new ImageObject(imgEl, {
          left: e.left,
          top: e.top,
          scaleX: (e.imageDiamater || 0) / (imgEl.naturalWidth || imgEl.width),
          scaleY: (e.imageDiamater || 0) / (imgEl.naturalHeight || imgEl.height),
        })

        const diaDiff = ((e.imageDiamater || 0) - (e.diameter || 0)) / 2

        if (this.drawables.signType == "acryl_alu_sign") {
          // screw holes for pdf
          const screwDiff = diaDiff + ((e.diameter || 0) - screwDiameter) / 2
          items.push(
            new CircleObject({
              left: (e.left || 0) + screwDiff,
              top: (e.top || 0) + screwDiff,
              radius: screwDiameter / 2,
              startAngle: 0,
              endAngle: Calc.toDeg(Math.PI * 2),
              fill: "transparent",
            }).addClass("hang", "screw")
          )
        }

        // hole placeholder for pdf
        items.push(
          new CircleObject({
            left: (e.left || 0) + diaDiff,
            top: (e.top || 0) + diaDiff,
            radius: (e.diameter || 0) / 2,
            startAngle: 0,
            endAngle: Calc.toDeg(Math.PI * 2),
            fill: "transparent",
          }).addClass("hang", "wall_distance")
        )

        items.push(img)
      }
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: this.state.widthPx,
        height: this.state.heightPx,
      }),
    ]
  }

  bottomDrawable() {
    const items = []
    const doublePlatedOffset = 6

    if (this.drawables.signType == "acryl_alu_sign") {
      for (const e of this.positions()) {
        const offsetColor = e.stretch ? "transparent" : "#c3cfd6"
        // offset
        items.push(
          new CircleObject({
            left: (e.left || 0) + doublePlatedOffset,
            top: (e.top || 0) - doublePlatedOffset,
            radius: (e.imageDiamater || 0) / 2,
            stroke: offsetColor,
            fill: offsetColor,
          })
        )

        // offset stretch
        items.push(
          new CircleObject({
            left: (e.left || 0) - doublePlatedOffset,
            top: (e.top || 0) + doublePlatedOffset,
            radius: (e.imageDiamater || 0) / 2,
            stroke: "transparent",
            fill: "transparent",
          })
        )
      }
    }

    return new GroupObject(items, {
      left: 0,
      top: 0,
      width: this.state.widthPx,
      height: this.state.heightPx,
    })
  }

  engravingDrawable() {
    const items = []

    for (const e of this.positions()) {
      if (e.stretch === true) {
        items.push(
          new CircleObject({
            left: e.left,
            top: e.top,
            radius: (e.diameter || 0) / 2,
            stroke: "transparent",
            strokeWidth: 1,
            fill: "transparent",
          })
        )

        continue
      }

      items.push(
        new CircleObject({
          left: e.left,
          top: e.top,
          radius: (e.diameter || 0) / 2,
          stroke: this.drawables.engravingColor,
          strokeWidth: this.drawables.engravingStrokeWidth,
          fill: "transparent",
        }).addClass("hang", "wall_distance")
      )
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: this.state.widthPx,
        height: this.state.heightPx,
      }),
    ]
  }

  autoSizePadding(state: SignState) {
    const { widthPx, heightPx } = state
    const fourScrews = Calc.pxToMm(widthPx) >= 100
    const extra = fourScrews && this.state.shape === "round" ? Math.min(widthPx, heightPx) * 0.1 : 0

    if (state.material === "wood") {
      return 130 + extra
    }

    return 150 + extra
  }

  protected imagePaths() {
    return "/images/signomatic/fix_methods/walldistance_v2.png"
  }

  private positions(): FixmethodPosition[] {
    const { width, height, shape, material } = this.state
    let pos = []
    let diameter = Calc.mmToPx(8.3)
    let imageDiamater = Calc.mmToPx(16)

    if (this.drawables.signType === "acryl_sign" && shape === "ellipse") {
      if (width < 200) {
        diameter = Calc.mmToPx(8.3)
        imageDiamater = Calc.mmToPx(13)
      } else {
        diameter = Calc.mmToPx(10)
        imageDiamater = Calc.mmToPx(19)
      }
    } else {
      if (width < 600 && height < 400) {
        diameter = Calc.mmToPx(8.3)
        imageDiamater = Calc.mmToPx(13)
      } else {
        diameter = Calc.mmToPx(10)
        imageDiamater = Calc.mmToPx(19)
      }
    }

    if (shape === "rotated_rect") {
      pos = this.rotatedRectPositions(diameter)
    } else if (this.drawables.signType == "acryl_alu_sign") {
      pos = this.acrylAluPositions(diameter)
    } else if (
      ["rounded_top", "rounded_sides", "curved_top", "arched_top"].includes(shape) &&
      this.state.metaProduct == "door_sign"
    ) {
      pos = this.doorPositions(diameter)
    } else if (
      ["rounded_top", "rounded_sides", "curved_top", "arched_top"].includes(shape) &&
      this.state.metaProduct == "house_sign"
    ) {
      pos = this.housePositions(diameter)
    } else if (material === "wood") {
      pos = this.woodPositions(diameter)
    } else if (shape === "triangle") {
      pos = this.trianglePositions(diameter, imageDiamater)
    } else if (["arrow_left", "arrow_right", "direction_left", "direction_right"].includes(shape)) {
      pos = this.arrowAndDirectionPositions(diameter)
    } else {
      pos = this.regularPositions(diameter)
    }

    return pos.map(position => ({ ...position, diameter, imageDiamater }))
  }

  private woodPositions(diameter: number) {
    const { width, widthPx, height, heightPx, shape } = this.state
    const hasBorder = this.drawables.hasBorder
    const pos = []
    let min = 0
    let max = 0

    const center = widthPx / 2 - diameter / 2
    const middle = heightPx / 2 - diameter / 2

    if (shape === "rect") {
      if (width >= 600) {
        const distance = Calc.mmToPx(18)
        const left = distance - diameter / 2
        const top = distance - diameter / 2
        const right = widthPx - diameter / 2 - distance
        const bottom = heightPx - diameter / 2 - distance

        pos.push({ left, top, diameter })
        pos.push({ left, top: bottom, diameter })
        pos.push({ left: right, top, diameter })
        pos.push({ left: right, top: bottom, diameter })
        pos.push({ left: center, top, diameter })
        pos.push({ left: center, top: bottom, diameter })
      } else if (height >= 100) {
        const distance = Calc.mmToPx(16)
        const left = distance - diameter / 2
        const top = distance - diameter / 2
        const right = widthPx - diameter / 2 - distance
        const bottom = heightPx - diameter / 2 - distance

        pos.push({ left, top, diameter })
        pos.push({ left, top: bottom, diameter })
        pos.push({ left: right, top, diameter })
        pos.push({ left: right, top: bottom, diameter })
      } else {
        const distance = Calc.mmToPx(11)
        const left = distance - diameter / 2
        const right = widthPx - diameter / 2 - distance

        pos.push({ left, top: middle, diameter })
        pos.push({ left: right, top: middle, diameter })
      }
    } else if (shape === "round") {
      if (width >= 600) {
        const distance = this.drawables.sizeRelative("width", Calc.mmToPx(35), Calc.mmToPx(55), {
          minWidth: 600,
        })
        const left = distance - diameter / 2
        const top = distance - diameter / 2
        const right = widthPx - diameter / 2 - distance
        const bottom = heightPx - diameter / 2 - distance

        pos.push({ left, top, diameter })
        pos.push({ left, top: bottom, diameter })
        pos.push({ left: right, top, diameter })
        pos.push({ left: right, top: bottom, diameter })
        pos.push({ left: center, top, diameter })
        pos.push({ left: center, top: bottom, diameter })
      } else if (height >= 100) {
        const distance = this.drawables.sizeRelative("width", Calc.mmToPx(14), Calc.mmToPx(35), {
          maxWidth: 599,
        })
        const left = distance - diameter / 2
        const top = distance - diameter / 2
        const right = widthPx - diameter / 2 - distance
        const bottom = heightPx - diameter / 2 - distance

        pos.push({ left, top, diameter })
        pos.push({ left, top: bottom, diameter })
        pos.push({ left: right, top, diameter })
        pos.push({ left: right, top: bottom, diameter })
      } else {
        const distance = Calc.mmToPx(11)
        const left = distance - diameter / 2
        const right = widthPx - diameter / 2 - distance

        pos.push({ left, top: middle, diameter })
        pos.push({ left: right, top: middle, diameter })
      }
    } else if (shape === "thorny") {
      if (width >= 600) {
        const distance = this.drawables.sizeRelative(
          "width",
          Calc.mmToPx(hasBorder ? 70 : 55),
          Calc.mmToPx(hasBorder ? 130 : 100),
          {
            minWidth: 600,
          }
        )

        const left = distance - diameter / 2
        const right = widthPx - diameter / 2 - distance

        const topdistance = this.drawables.sizeRelative("width", Calc.mmToPx(hasBorder ? 65 : 55), Calc.mmToPx(80), {
          minWidth: 1000,
        })

        const top = topdistance - diameter / 2
        const bottom = heightPx - diameter / 2 - topdistance

        pos.push({ left, top: middle, diameter })
        pos.push({ left: right, top: middle, diameter })
        pos.push({ left: center, top, diameter })
        pos.push({ left: center, top: bottom, diameter })
      } else {
        min = Calc.mmToPx(hasBorder ? 13 : 12)
        max = Calc.mmToPx(hasBorder ? 70 : 55)

        const distance = this.drawables.sizeRelative("width", min, max, { maxWidth: 599 })

        const left = distance - diameter / 2
        const right = widthPx - diameter / 2 - distance

        pos.push({ left, top: middle, diameter })
        pos.push({ left: right, top: middle, diameter })
      }
    } else if (shape === "ellipse" || shape === "stop") {
      if (width >= 1000) {
        let distance = 0

        if (shape === "ellipse") {
          distance = this.drawables.sizeRelative("width", Calc.mmToPx(23), Calc.mmToPx(28), {
            minWidth: 1000,
          })
        } else if (shape === "stop") {
          distance = Calc.mmToPx(22)
        }

        const left = distance - diameter / 2
        const top = distance - diameter / 2
        const right = widthPx - diameter / 2 - distance
        const bottom = heightPx - diameter / 2 - distance

        pos.push({ left, top: middle, diameter })
        pos.push({ left: right, top: middle, diameter })
        pos.push({ left: center, top, diameter })
        pos.push({ left: center, top: bottom, diameter })
      } else {
        if (shape === "ellipse") {
          min = Calc.mmToPx(hasBorder ? 14 : 12)
          max = Calc.mmToPx(hasBorder ? 25 : 23)
        } else if (shape === "stop") {
          min = Calc.mmToPx(18)
          max = Calc.mmToPx(18)
        }

        const thornExtra = hasBorder ? this.thornDistance : 0
        const distance = this.drawables.sizeRelative("height", min, max)

        const left = thornExtra + distance - diameter / 2
        const right = widthPx - thornExtra - diameter / 2 - distance

        pos.push({ left, top: middle, diameter })
        pos.push({ left: right, top: middle, diameter })
      }
    } else if (["arrow_left", "arrow_right", "direction_left", "direction_right"].includes(shape)) {
      const minFront = Calc.mmToPx(15)
      const maxFront = Calc.mmToPx(25)
      const minBack = Calc.mmToPx(14)
      const maxBack = Calc.mmToPx(20)

      let distanceFront = this.drawables.sizeRelative("width", minFront, maxFront)
      let distanceBack = this.drawables.sizeRelative("width", minBack, maxBack)

      if (["direction_left", "direction_right"].includes(shape)) {
        distanceFront = this.drawables.sizeRelative("width", minFront, maxFront, { maxWidth: 295 })
        distanceBack = this.drawables.sizeRelative("width", minBack, maxBack, { maxWidth: 295 })
      }
      let left = distanceFront - diameter / 2
      let right = widthPx - diameter / 2 - distanceBack

      if (shape === "arrow_right") {
        left = distanceBack - diameter / 2
        right = widthPx - diameter / 2 - distanceFront

        pos.push({
          left: widthPx - diameter / 2 - distanceBack,
          top: middle,
          diameter,
          stretch: true,
        })
      } else {
        pos.push({
          left: distanceBack - diameter / 2,
          top: middle,
          diameter,
          stretch: true,
        })
      }

      pos.push({ left, top: middle, diameter })
      pos.push({ left: right, top: middle, diameter })
    } else if (shape === "triangle") {
      min = Calc.mmToPx(8)
      max = Calc.mmToPx(25)
      const distance = this.drawables.sizeRelative("height", min, max)
      const offset = Calc.triangleHorizontalOffset(widthPx, heightPx, distance)

      const minTop = Calc.mmToPx(hasBorder ? 15 : 14)
      const maxTop = Calc.mmToPx(hasBorder ? 40 : 35)
      const distanceTop = this.drawables.sizeRelative("height", minTop, maxTop)

      const left = offset + distance - diameter / 2
      const top = distanceTop - diameter / 2
      const bottom = heightPx - distance - diameter / 2
      const right = widthPx - offset - distance - diameter / 2
      const topStretch = distance - diameter / 2

      pos.push({ left: center, top, diameter })
      pos.push({ left, top: bottom, diameter })
      pos.push({ left: right, top: bottom, diameter })
      pos.push({
        left: center,
        top: topStretch,
        diameter,
        stretch: true,
      })
    }

    return pos
  }

  private trianglePositions(diameter: number, imageDiamater: number) {
    const { widthPx, heightPx } = this.state
    const pos = []
    const center = widthPx / 2 - diameter / 2

    const min = Calc.mmToPx(9)
    const max = Calc.mmToPx(17)
    const distance = this.drawables.sizeRelative("height", min, max, {
      minHeight: 100,
      maxHeight: 1000,
    })

    const offset = Calc.triangleHorizontalOffset(widthPx, heightPx, distance)
    const minTop = Calc.mmToPx(15)
    const maxTop = Calc.mmToPx(35)
    const distanceTop = this.drawables.sizeRelative("height", minTop, maxTop, {
      minHeight: 100,
      maxHeight: 1000,
    })

    const left = offset + distance - diameter / 2
    const top = distanceTop - diameter / 2
    const bottom = heightPx - distance - diameter / 2
    const right = widthPx - offset - distance - diameter / 2
    const topStretch = distance - imageDiamater / 2

    pos.push({ left: center, top, diameter })
    pos.push({ left, top: bottom, diameter })
    pos.push({ left: right, top: bottom, diameter })
    pos.push({ left: center, top: topStretch, diameter, stretch: true })
    return pos
  }

  private rotatedRectPositions(diameter: number) {
    const { widthPx, heightPx, height } = this.state
    const pos = []
    const fourHoles = height > 99

    let distance = fourHoles
      ? this.drawables.sizeRelative("width", Calc.mmToPx(18), Calc.mmToPx(28), { maxWidth: 600 })
      : Calc.mmToPx(18)

    if (this.drawables.signType === "wood_sign") {
      distance = fourHoles
        ? this.drawables.sizeRelative("width", Calc.mmToPx(18), Calc.mmToPx(40), { maxWidth: 600 })
        : Calc.mmToPx(18)
    }

    if (this.drawables.signType === "aluminium_sign") {
      distance = fourHoles
        ? this.drawables.sizeRelative("width", Calc.mmToPx(14), Calc.mmToPx(25), { maxWidth: 600 })
        : Calc.mmToPx(14)
    }

    const center = widthPx / 2 - diameter / 2
    const centerV = heightPx / 2 - diameter / 2
    const top = distance - diameter / 2
    const bottom = heightPx - diameter / 2 - distance
    const left = distance - diameter / 2
    const right = widthPx - diameter / 2 - distance

    if (fourHoles) {
      pos.push({ left: center, top: top })
      pos.push({ left: center, top: bottom })
    }

    pos.push({ left, top: centerV })
    pos.push({ left: right, top: centerV })

    return pos
  }

  private arrowAndDirectionPositions(diameter: number) {
    const { widthPx, heightPx, shape } = this.state
    const pos = []

    const distanceFront = this.drawables.sizeRelative("width", Calc.mmToPx(15), Calc.mmToPx(25), { maxWidth: 600 })
    const distanceBack = this.drawables.sizeRelative("width", Calc.mmToPx(14), Calc.mmToPx(20), { maxWidth: 600 })

    const centerV = heightPx / 2 - diameter / 2
    let left = distanceFront - diameter / 2
    let right = widthPx - diameter / 2 - distanceBack

    if (shape === "direction_right" || shape == "arrow_right") {
      left = distanceBack - diameter / 2
      right = widthPx - diameter / 2 - distanceFront

      pos.push({
        left: widthPx - diameter / 2 - distanceBack,
        top: centerV,
        stretch: true,
      })
    } else {
      pos.push({
        left: distanceBack - diameter / 2,
        top: centerV,
        stretch: true,
      })
    }

    pos.push({ left, top: centerV, diameter })
    pos.push({ left: right, top: centerV, diameter })

    return pos
  }

  private acrylAluPositions(diameter: number) {
    const { height, widthPx, heightPx, shape } = this.state
    const pos = []

    let distanceSide = 0
    let distanceTop = 0

    let fourScrews = height >= 199 && height < 600
    let sixScrews = height > 599

    if (shape === "ellipse") {
      fourScrews = sixScrews = false
    }

    if (fourScrews) {
      const relOptions = { minWidth: 199, maxWidth: 599, minHeight: 199, maxHeight: 599 }
      if (shape === "rect") {
        distanceTop = distanceSide = Calc.mmToPx(14)
      } else if (shape === "round") {
        distanceTop = distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(14), Calc.mmToPx(30), relOptions)
      } else if (shape == "arched_top") {
        distanceSide = Calc.mmToPx(12)
        distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(79), Calc.mmToPx(220), relOptions)
      } else if (shape == "rounded_top") {
        distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(12), Calc.mmToPx(17), relOptions)
        distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(30), Calc.mmToPx(80), relOptions)
      } else if (shape == "rounded_sides") {
        distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(30), Calc.mmToPx(80), relOptions)
        distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(12), Calc.mmToPx(16), relOptions)
      } else if (shape == "curved_top") {
        const distance = Calc.mmToPx(12)
        const curveTop = this.drawables.sizeRelative("height", Calc.mmToPx(85), Calc.mmToPx(247), relOptions)

        const left = distance - diameter / 2
        const top = distance - diameter / 2
        const right = widthPx - distance - diameter / 2
        const bottom = heightPx - Calc.mmToPx(12) - diameter / 2

        pos.push({ left, top })
        pos.push({ left, top: bottom })
        pos.push({ left: right, top: curveTop })
        pos.push({ left: right, top: bottom })

        return pos
      }
    } else if (sixScrews) {
      const relOptions = { minHeight: 200 }
      if (shape === "rect") {
        distanceTop = distanceSide = Calc.mmToPx(14)
      } else if (shape === "round") {
        distanceTop = distanceSide = Calc.mmToPx(30)
      } else if (shape == "arched_top") {
        distanceSide = Calc.mmToPx(12)
        distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(85), Calc.mmToPx(220), relOptions)
      } else if (shape == "rounded_top") {
        distanceSide = Calc.mmToPx(30)
        distanceTop = Calc.mmToPx(80)
      } else if (shape == "rounded_sides") {
        distanceSide = Calc.mmToPx(80)
        distanceTop = Calc.mmToPx(30)
      } else if (shape == "curved_top") {
        const distance = Calc.mmToPx(12)
        const curveTop = this.drawables.sizeRelative("height", Calc.mmToPx(90), Calc.mmToPx(247), relOptions)
        const middleTop = this.drawables.sizeRelative("height", Calc.mmToPx(30), Calc.mmToPx(60), relOptions)
        const centerH = widthPx / 2 - diameter / 2

        const left = distance - diameter / 2
        const top = distance - diameter / 2
        const right = widthPx - distance - diameter / 2
        const bottom = heightPx - Calc.mmToPx(12) - diameter / 2

        pos.push({ left, top })
        pos.push({ left, top: bottom })
        pos.push({ left: right, top: curveTop })
        pos.push({ left: right, top: bottom })
        pos.push({ left: centerH, top: middleTop })
        pos.push({ left: centerH, top: bottom })

        return pos
      }
    } else {
      if (shape === "ellipse") {
        distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(12), Calc.mmToPx(18), {
          maxWidth: 400,
          maxHeight: 400,
        })
      } else {
        distanceSide = Calc.mmToPx(12)
      }
    }

    const left = distanceSide - diameter / 2
    const top = distanceTop - diameter / 2
    let bottom = heightPx - distanceTop - diameter / 2
    const right = widthPx - distanceSide - diameter / 2
    let centerV = widthPx / 2 - diameter / 2
    let middleTop = top
    let middleBottom = bottom

    if (shape == "arched_top") {
      bottom = heightPx - distanceSide - diameter / 2
      middleBottom = heightPx - distanceSide - diameter / 2
      if (fourScrews) {
        pos.push({
          left: widthPx / 2 - diameter / 2,
          top: diameter / 2,
          stretch: true,
        })
      } else if (sixScrews) {
        middleTop = distanceSide - diameter / 2
      } else {
        // arcH constant also in arched_top
        const arcH = heightPx * 0.35
        const squareHeight = heightPx - arcH
        centerV = arcH + squareHeight / 2 - diameter / 2
        pos.push({
          left: widthPx / 2 - diameter / 2,
          top: heightPx - centerV - diameter,
          stretch: true,
        })
      }
    } else if (shape == "rounded_top" && sixScrews) {
      middleTop = distanceSide - diameter / 2
      middleBottom = heightPx - distanceSide - diameter / 2
    }

    if (sixScrews) {
      pos.push({ left, top })
      pos.push({ left, top: bottom })
      pos.push({ left: right, top })
      pos.push({ left: right, top: bottom })
      pos.push({ left: centerV, top: middleTop })
      pos.push({ left: centerV, top: middleBottom })
    } else if (fourScrews) {
      pos.push({ left, top })
      pos.push({ left, top: bottom })
      pos.push({ left: right, top })
      pos.push({ left: right, top: bottom })
    } else {
      pos.push({ left, top: centerV })
      pos.push({ left: right, top: centerV })
    }

    return pos
  }

  private doorPositions(diameter: number) {
    const { widthPx, height, heightPx, shape } = this.state
    const pos = []

    let distanceSide = 0
    let distanceTop = 0

    const fourScrews = height >= 199
    if (fourScrews) {
      const relOptions = { minHeight: 200, minWidth: 200 }
      if (this.drawables.signType === "aluminium_sign") {
        if (shape == "arched_top") {
          distanceSide = Calc.mmToPx(12)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(79), Calc.mmToPx(220), relOptions)
        } else if (shape == "rounded_top") {
          distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(12), Calc.mmToPx(17), relOptions)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(30), Calc.mmToPx(80), relOptions)
        } else if (shape == "rounded_sides") {
          distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(30), Calc.mmToPx(80), relOptions)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(12), Calc.mmToPx(16), relOptions)
        } else if (shape == "curved_top") {
          const distance = Calc.mmToPx(12)
          const curveTop = this.drawables.sizeRelative("height", Calc.mmToPx(85), Calc.mmToPx(247), relOptions)

          const left = distance - diameter / 2
          const top = distance - diameter / 2
          const right = widthPx - distance - diameter / 2
          const bottom = heightPx - Calc.mmToPx(12) - diameter / 2

          pos.push({ left, top })
          pos.push({ left, top: bottom })
          pos.push({ left: right, top: curveTop })
          pos.push({ left: right, top: bottom })

          return pos
        }
      } else if (["acryl_sign", "wood_sign"].includes(this.drawables.signType)) {
        if (shape == "arched_top") {
          distanceSide = Calc.mmToPx(12)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(79), Calc.mmToPx(220), relOptions)
        } else if (shape == "rounded_top") {
          distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(12), Calc.mmToPx(17), relOptions)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(30), Calc.mmToPx(80), relOptions)
        } else if (shape == "rounded_sides") {
          distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(30), Calc.mmToPx(80), relOptions)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(12), Calc.mmToPx(16), relOptions)
        } else if (shape == "curved_top") {
          const distance = Calc.mmToPx(12)
          const curveTop = this.drawables.sizeRelative("height", Calc.mmToPx(85), Calc.mmToPx(247), relOptions)

          const left = distance - diameter / 2
          const top = distance - diameter / 2
          const right = widthPx - distance - diameter / 2
          const bottom = heightPx - Calc.mmToPx(12) - diameter / 2

          pos.push({ left, top })
          pos.push({ left, top: bottom })
          pos.push({ left: right, top: curveTop })
          pos.push({ left: right, top: bottom })

          return pos
        }
      }
    } else {
      distanceSide = Calc.mmToPx(12)
      distanceTop = distanceSide
    }

    const left = distanceSide - diameter / 2
    let top = distanceTop - diameter / 2
    let bottom = heightPx - distanceTop - diameter / 2
    const right = widthPx - distanceSide - diameter / 2
    let centerV = widthPx / 2 - diameter / 2

    if (shape == "arched_top" && fourScrews) {
      top = distanceTop - diameter / 2
      bottom = heightPx - distanceSide - diameter / 2
      pos.push({
        left: widthPx / 2 - diameter / 2,
        top: diameter / 2,
        stretch: true,
      })
    } else if (shape == "arched_top") {
      // arcH constant also in arched_top
      const arcH = heightPx * 0.35
      const squareHeight = heightPx - arcH
      centerV = arcH + squareHeight / 2 - diameter / 2
      pos.push({
        left: widthPx / 2 - diameter / 2,
        top: heightPx - centerV - diameter,
        stretch: true,
      })
    }

    if (fourScrews) {
      pos.push({ left, top })
      pos.push({ left, top: bottom })
      pos.push({ left: right, top })
      pos.push({ left: right, top: bottom })
    } else {
      pos.push({ left, top: centerV })
      pos.push({ left: right, top: centerV })
    }

    return pos
  }

  private housePositions(diameter: number) {
    const { width, widthPx, heightPx, shape } = this.state
    const pos = []

    let distanceSide = 0
    let distanceTop = 0

    const fourScrews = width >= 199 && width < 600
    const sixScrews = width > 599

    if (fourScrews) {
      const relOptions = { minWidth: 199, maxWidth: 599, minHeight: 199, maxHeight: 599 }
      if (this.drawables.signType === "acryl_sign") {
        if (shape == "arched_top") {
          distanceSide = Calc.mmToPx(12)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(79), Calc.mmToPx(228), relOptions)
        } else if (shape == "rounded_top") {
          distanceSide = Calc.mmToPx(12)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(32), Calc.mmToPx(66), relOptions)
        } else if (shape == "rounded_sides") {
          distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(32), Calc.mmToPx(72), relOptions)
          distanceTop = Calc.mmToPx(12)
        } else if (shape == "curved_top") {
          const distance = this.drawables.sizeRelative("height", Calc.mmToPx(12), Calc.mmToPx(14), relOptions)
          const curveTop = this.drawables.sizeRelative("height", Calc.mmToPx(84), Calc.mmToPx(250), relOptions)

          const left = distance - diameter / 2
          const top = distance - diameter / 2
          const right = widthPx - distance - diameter / 2
          const bottom = heightPx - Calc.mmToPx(12) - diameter / 2

          pos.push({ left, top })
          pos.push({ left, top: bottom })
          pos.push({ left: right, top: curveTop })
          pos.push({ left: right, top: bottom })

          return pos
        }
      } else if (this.drawables.signType == "aluminium_sign") {
        if (shape == "arched_top") {
          distanceSide = Calc.mmToPx(12)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(80), Calc.mmToPx(225), relOptions)
        } else if (shape == "rounded_top") {
          distanceSide = Calc.mmToPx(12)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(30), Calc.mmToPx(71), relOptions)
        } else if (shape == "rounded_sides") {
          distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(30), Calc.mmToPx(71), relOptions)
          distanceTop = Calc.mmToPx(12)
        } else if (shape == "curved_top") {
          const distance = this.drawables.sizeRelative("height", Calc.mmToPx(12), Calc.mmToPx(14), relOptions)
          const curveTop = this.drawables.sizeRelative("height", Calc.mmToPx(84), Calc.mmToPx(250), relOptions)

          const left = distance - diameter / 2
          const top = distance - diameter / 2
          const right = widthPx - distance - diameter / 2
          const bottom = heightPx - Calc.mmToPx(12) - diameter / 2

          pos.push({ left, top })
          pos.push({ left, top: bottom })
          pos.push({ left: right, top: curveTop })
          pos.push({ left: right, top: bottom })

          return pos
        }
      }
    } else if (sixScrews) {
      const relOptions = { minWidth: 600, minHeight: 200, maxHeight: 600, maxWidth: 1000 }
      if (this.drawables.signType === "acryl_sign") {
        if (shape == "arched_top") {
          distanceSide = Calc.mmToPx(12)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(85), Calc.mmToPx(225), relOptions)
        } else if (shape == "rounded_top") {
          distanceSide = Calc.mmToPx(12)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(32), Calc.mmToPx(75), relOptions)
        } else if (shape == "rounded_sides") {
          distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(71), Calc.mmToPx(110), relOptions)
          distanceTop = Calc.mmToPx(12)
        } else if (shape == "curved_top") {
          const distance = this.drawables.sizeRelative("width", Calc.mmToPx(14), Calc.mmToPx(16), relOptions)
          const curveTop = this.drawables.sizeRelative("height", Calc.mmToPx(90), Calc.mmToPx(250), relOptions)
          const middleTop = this.drawables.sizeRelative("height", Calc.mmToPx(30), Calc.mmToPx(60), relOptions)
          const centerH = widthPx / 2 - diameter / 2

          const left = distance - diameter / 2
          const top = distance - diameter / 2
          const right = widthPx - distance - diameter / 2
          const bottom = heightPx - Calc.mmToPx(12) - diameter / 2

          pos.push({ left, top })
          pos.push({ left, top: bottom })
          pos.push({ left: right, top: curveTop })
          pos.push({ left: right, top: bottom })
          pos.push({ left: centerH, top: middleTop })
          pos.push({ left: centerH, top: bottom })

          return pos
        }
      } else if (this.drawables.signType == "aluminium_sign") {
        if (shape == "arched_top") {
          distanceSide = Calc.mmToPx(15)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(90), Calc.mmToPx(230), relOptions)
        } else if (shape == "rounded_top") {
          distanceSide = Calc.mmToPx(15)
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(35), Calc.mmToPx(80), relOptions)
        } else if (shape == "rounded_sides") {
          distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(71), Calc.mmToPx(110), relOptions)
          distanceTop = Calc.mmToPx(15)
        } else if (shape == "curved_top") {
          const distance = this.drawables.sizeRelative("width", Calc.mmToPx(13), Calc.mmToPx(15), relOptions)
          const curveTop = this.drawables.sizeRelative("height", Calc.mmToPx(90), Calc.mmToPx(250), relOptions)
          const middleTop = this.drawables.sizeRelative("height", Calc.mmToPx(30), Calc.mmToPx(60), relOptions)
          const centerH = widthPx / 2 - diameter / 2

          const left = distance - diameter / 2
          const top = distance - diameter / 2
          const right = widthPx - distance - diameter / 2
          const bottom = heightPx - Calc.mmToPx(15) - diameter / 2

          pos.push({ left, top })
          pos.push({ left, top: bottom })
          pos.push({ left: right, top: curveTop })
          pos.push({ left: right, top: bottom })
          pos.push({ left: centerH, top: middleTop })
          pos.push({ left: centerH, top: bottom })

          return pos
        }
      }
    } else {
      distanceSide = Calc.mmToPx(12)
    }

    const left = distanceSide - diameter / 2
    const top = distanceTop - diameter / 2
    let bottom = heightPx - distanceTop - diameter / 2
    const right = widthPx - distanceSide - diameter / 2
    let centerV = widthPx / 2 - diameter / 2
    let middleTop = top
    let middleBottom = bottom

    if (shape == "arched_top") {
      bottom = heightPx - distanceSide - diameter / 2
      middleBottom = heightPx - distanceSide - diameter / 2
      if (fourScrews) {
        pos.push({
          left: widthPx / 2 - diameter / 2,
          top: diameter / 2,
          stretch: true,
        })
      } else if (sixScrews) {
        middleTop = distanceSide - diameter / 2
      } else {
        // arcH constant also in arched_top
        const arcH = heightPx * 0.35
        const squareHeight = heightPx - arcH
        centerV = arcH + squareHeight / 2 - diameter / 2
        pos.push({
          left: widthPx / 2 - diameter / 2,
          top: heightPx - centerV - diameter,
          stretch: true,
        })
      }
    } else if (shape == "rounded_top" && sixScrews) {
      middleTop = distanceSide - diameter / 2
      middleBottom = heightPx - distanceSide - diameter / 2
    }

    if (sixScrews) {
      pos.push({ left, top })
      pos.push({ left, top: bottom })
      pos.push({ left: right, top })
      pos.push({ left: right, top: bottom })
      pos.push({ left: centerV, top: middleTop })
      pos.push({ left: centerV, top: middleBottom })
    } else if (fourScrews) {
      pos.push({ left, top })
      pos.push({ left, top: bottom })
      pos.push({ left: right, top })
      pos.push({ left: right, top: bottom })
    } else {
      pos.push({ left, top: centerV })
      pos.push({ left: right, top: centerV })
    }

    return pos
  }

  private regularPositions(diameter: number) {
    const { width, widthPx, height, heightPx, shape } = this.state
    const pos = []
    let center = widthPx / 2 - diameter / 2
    let distance = 0
    let left = 0
    let top = 0
    let bottom = 0
    let right = 0

    let fourScrews = height >= 100
    let sixScrews = ["aluminium_sign", "acryl_sign"].indexOf(this.drawables.signType) > -1 && width >= 600

    if (shape === "ellipse" || shape == "stop") {
      fourScrews = sixScrews = false
    }

    if (this.drawables.signType === "aluminium_sign") {
      if (shape === "rect" || shape == "stop") {
        distance = Calc.mmToPx(14)
      } else if (shape === "round") {
        if (sixScrews) {
          distance = this.drawables.sizeRelative("width", Calc.mmToPx(25), Calc.mmToPx(30), {
            minWidth: 999,
          })
        } else if (fourScrews) {
          distance = this.drawables.sizeRelative("height", Calc.mmToPx(14), Calc.mmToPx(25), {
            minHeight: 99,
            maxHeight: 600,
          })
        } else {
          distance = Calc.mmToPx(14)
        }
      } else if (shape === "ellipse") {
        distance = this.drawables.sizeRelative("width", Calc.mmToPx(12), Calc.mmToPx(16))
      }
    } else if (this.drawables.signType === "acryl_sign") {
      if (shape === "rect") {
        distance = Calc.mmToPx(14)
      } else if (shape === "ellipse") {
        distance = this.drawables.sizeRelative("width", Calc.mmToPx(12), Calc.mmToPx(18), {
          maxWidth: 400,
          maxHeight: 400,
        })
      } else if (shape === "round") {
        if (sixScrews) {
          distance = this.drawables.sizeRelative("width", Calc.mmToPx(30), Calc.mmToPx(35), {
            minWidth: 999,
          })
        } else if (fourScrews) {
          distance = this.drawables.sizeRelative("height", Calc.mmToPx(14), Calc.mmToPx(30), {
            minHeight: 99,
            maxHeight: 600,
          })
        } else {
          distance = Calc.mmToPx(14)
        }
      }
    }

    left = distance - diameter / 2
    top = distance - diameter / 2
    bottom = heightPx - distance - diameter / 2
    right = widthPx - distance - diameter / 2

    if (sixScrews) {
      pos.push({ left: center, top, diameter })
      pos.push({ left: center, top: bottom, diameter })
    }

    if (fourScrews || sixScrews) {
      pos.push({ left, top, diameter })
      pos.push({ left, top: bottom, diameter })
      pos.push({ left: right, top, diameter })
      pos.push({ left: right, top: bottom, diameter })
    } else {
      center = heightPx / 2 - diameter / 2
      pos.push({ left, top: center, diameter })
      pos.push({ left: right, top: center, diameter })
    }

    return pos
  }
}
